import * as Sentry from '@sentry/browser';
import { PostHog } from 'posthog-js';

export const CHROME_VIEWER_EVENT = 'chrome_viewer_lifecycle';
export const CHROME_VIEWER_SENTRY_MESSAGE = 'Chrome viewer record fetch failed';
export const CHROME_VIEWER_TELEMETRY_CONTRACT = 'chrome_viewer_record_v1';

const SENTRY_SDK = {
  name: 'sentry.javascript.browser',
  version: Sentry.SDK_VERSION,
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const EVENT_ID_PATTERN = /^[0-9a-f]{32}$/;

const STAGE_CONTRACTS = {
  component_mounted: {},
  record_fetch_started: { fetch: true },
  record_fetch_succeeded: { fetch: true, terminalOutcome: 'succeeded' },
  record_fetch_failed: {
    fetch: true,
    terminalOutcome: 'failed',
    statusClass: true,
  },
  record_fetch_cancelled: { fetch: true, terminalOutcome: 'cancelled' },
  record_retry_clicked: { fetch: true },
};
const DURATION_BUCKETS = ['lt_1s', '1s_to_lt_3s', '3s_to_lt_10s', 'gte_10s'];
const STATUS_CLASSES = ['no_response', 'http_4xx', 'http_5xx', 'other_http', 'missing_content'];
const DEPLOYMENT_TYPES = ['stable', 'canary'];
const LIFECYCLE_PROPERTY_FIELDS = [
  'fetch_id',
  'terminal_outcome',
  'duration_bucket',
  'status_class',
  'retry_ordinal',
];
const LIFECYCLE_FIELD_NAMES = [
  'page_boot_id',
  'deployment_type',
  ...LIFECYCLE_PROPERTY_FIELDS,
];
const POSTHOG_CONTRACT_PROPERTIES = [
  'distinct_id',
  'stage',
  'deployment_type',
  ...LIFECYCLE_PROPERTY_FIELDS,
];
const CHROME_VIEWER_CAPTURE_OPTIONS = { send_instantly: true };
const POSTHOG_SDK_PROPERTIES = new Set([
  'token',
  '$active_feature_flags',
  '$anon_distinct_id',
  '$browser',
  '$browser_language',
  '$browser_language_prefix',
  '$browser_type',
  '$browser_version',
  '$config_defaults',
  '$configured_session_timeout_ms',
  '$current_url',
  '$device_id',
  '$device_type',
  '$feature_flag_payloads',
  '$groups',
  '$had_persisted_distinct_id',
  '$host',
  '$initialization_time',
  '$insert_id',
  '$is_identified',
  '$lib',
  '$lib_custom_api_host',
  '$lib_rate_limit_remaining_tokens',
  '$lib_version',
  '$os',
  '$pathname',
  '$process_person_profile',
  '$raw_user_agent',
  '$recording_status',
  '$referrer',
  '$referring_domain',
  '$screen_height',
  '$screen_width',
  '$sdk_debug_error_capturing_properties',
  '$sdk_debug_extensions_init_method',
  '$sdk_debug_extensions_init_time_ms',
  '$sdk_debug_replay_flushed_size',
  '$sdk_debug_replay_internal_buffer_length',
  '$sdk_debug_replay_internal_buffer_size',
  '$sdk_debug_retry_queue_size',
  '$session_entry_pathname',
  '$session_entry_referrer',
  '$session_entry_referring_domain',
  '$session_entry_url',
  '$session_id',
  '$time',
  '$timezone',
  '$timezone_offset',
  '$user_id',
  '$viewport_height',
  '$viewport_width',
  '$window_id',
]);

const hasExactKeys = (value, allowedKeys) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const allowedKeySet = new Set(allowedKeys);
  return Object.keys(value).every((key) => allowedKeySet.has(key));
};

const isDateLike = (value) => (
  (value instanceof Date && Number.isFinite(value.getTime()))
  || (typeof value === 'string' && Number.isFinite(Date.parse(value)))
);

const randomHex = (byteLength) => {
  const bytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

let fallbackIdSequence = 0;
const fallbackPageSeed = (() => {
  try {
    return Math.floor(Math.random() * 0x100000000).toString(16).padStart(8, '0');
  } catch {
    return '00000000';
  }
})();

const formatUuidV4 = (value) => {
  const hex = value.split('');
  hex[12] = '4';
  hex[16] = (8 + (parseInt(hex[16], 16) % 4)).toString(16);
  return [
    hex.slice(0, 8).join(''),
    hex.slice(8, 12).join(''),
    hex.slice(12, 16).join(''),
    hex.slice(16, 20).join(''),
    hex.slice(20).join(''),
  ].join('-');
};

const fallbackChromeViewerId = () => {
  fallbackIdSequence += 1;
  const timestamp = Date.now().toString(16).padStart(12, '0').slice(-12);
  const sequence = fallbackIdSequence.toString(16).padStart(12, '0').slice(-12);
  return formatUuidV4(`${timestamp}${fallbackPageSeed}${sequence}`);
};

export const generateChromeViewerId = () => {
  try {
    if (typeof window.crypto?.randomUUID === 'function') {
      return window.crypto.randomUUID().toLowerCase();
    }

    return formatUuidV4(randomHex(16));
  } catch {
    return fallbackChromeViewerId();
  }
};

const PAGE_BOOT_ID = generateChromeViewerId();

export const durationBucketForMs = (durationMs) => {
  if (!Number.isFinite(durationMs) || durationMs < 0) {
    return null;
  }
  if (durationMs < 1000) {
    return 'lt_1s';
  }
  if (durationMs < 3000) {
    return '1s_to_lt_3s';
  }
  if (durationMs < 10000) {
    return '3s_to_lt_10s';
  }
  return 'gte_10s';
};

export const statusClassForStatus = (status, { missingContent = false } = {}) => {
  if (missingContent) {
    return 'missing_content';
  }
  if (status === undefined || status === null) {
    return 'no_response';
  }
  if (Number.isInteger(status) && status >= 400 && status < 500) {
    return 'http_4xx';
  }
  if (Number.isInteger(status) && status >= 500 && status < 600) {
    return 'http_5xx';
  }
  return 'other_http';
};

export const retryOrdinalForCount = (count) => {
  if (!Number.isInteger(count) || count <= 0) {
    return 0;
  }
  return Math.min(count, 3);
};

const deploymentType = () => {
  try {
    const value = document
      .querySelector('meta[name="m8-deployment-type"]')
      ?.getAttribute('content');
    return DEPLOYMENT_TYPES.includes(value) ? value : 'stable';
  } catch {
    return 'stable';
  }
};

const validLifecycleFields = (stage, fields) => {
  const contract = STAGE_CONTRACTS[stage];
  if (!contract || !hasExactKeys(fields, LIFECYCLE_FIELD_NAMES)) {
    return false;
  }
  if (!UUID_V4_PATTERN.test(fields.page_boot_id) || !DEPLOYMENT_TYPES.includes(fields.deployment_type)) {
    return false;
  }

  if (Boolean(contract.fetch) !== UUID_V4_PATTERN.test(fields.fetch_id || '')) {
    return false;
  }
  if (contract.fetch && ![0, 1, 2, 3].includes(fields.retry_ordinal)) {
    return false;
  }
  if (!contract.fetch && fields.retry_ordinal !== undefined) {
    return false;
  }

  const isTerminal = Boolean(contract.terminalOutcome);
  if (isTerminal !== DURATION_BUCKETS.includes(fields.duration_bucket)) {
    return false;
  }
  if (isTerminal && fields.terminal_outcome !== contract.terminalOutcome) {
    return false;
  }
  if (!isTerminal && fields.terminal_outcome !== undefined) {
    return false;
  }

  if (contract.statusClass) {
    return STATUS_CLASSES.includes(fields.status_class);
  }
  return fields.status_class === undefined;
};

const lifecycleProperties = (stage, fields) => {
  const properties = {
    distinct_id: fields.page_boot_id,
    stage,
    deployment_type: fields.deployment_type,
  };
  LIFECYCLE_PROPERTY_FIELDS.forEach((key) => {
    if (fields[key] !== undefined) {
      properties[key] = fields[key];
    }
  });
  return properties;
};

const hasOnlyKnownPostHogProperties = (properties) => Object.keys(properties).every((key) => (
  POSTHOG_CONTRACT_PROPERTIES.includes(key)
  || POSTHOG_SDK_PROPERTIES.has(key)
  || key.startsWith('$feature/')
));

export const projectChromeViewerPostHogEvent = (event) => {
  if (event?.event !== CHROME_VIEWER_EVENT) {
    return event;
  }
  if (!hasExactKeys(event, ['uuid', 'event', 'timestamp', 'properties'])
    || !UUID_PATTERN.test(event.uuid || '')
    || !isDateLike(event.timestamp)
    || !hasOnlyKnownPostHogProperties(event.properties || {})) {
    return null;
  }

  const fields = {
    page_boot_id: event.properties.distinct_id,
    fetch_id: event.properties.fetch_id,
    terminal_outcome: event.properties.terminal_outcome,
    duration_bucket: event.properties.duration_bucket,
    status_class: event.properties.status_class,
    retry_ordinal: event.properties.retry_ordinal,
    deployment_type: event.properties.deployment_type,
  };
  if (!validLifecycleFields(event.properties.stage, fields)
    || typeof event.properties.token !== 'string'
    || event.properties.token.length === 0) {
    return null;
  }

  return {
    uuid: event.uuid,
    event: CHROME_VIEWER_EVENT,
    timestamp: event.timestamp,
    properties: {
      token: event.properties.token,
      ...lifecycleProperties(event.properties.stage, fields),
      $process_person_profile: false,
    },
  };
};

const sentryTagsFromFields = (fields) => ({
  telemetry_contract: CHROME_VIEWER_TELEMETRY_CONTRACT,
  stage: 'record_fetch_failed',
  terminal_outcome: fields.terminal_outcome,
  duration_bucket: fields.duration_bucket,
  status_class: fields.status_class,
  retry_ordinal: fields.retry_ordinal,
  deployment_type: fields.deployment_type,
});

const validSentryTags = (tags) => (
  tags?.telemetry_contract === CHROME_VIEWER_TELEMETRY_CONTRACT
  && tags.stage === 'record_fetch_failed'
  && tags.terminal_outcome === 'failed'
  && DURATION_BUCKETS.includes(tags.duration_bucket)
  && STATUS_CLASSES.includes(tags.status_class)
  && [0, 1, 2, 3].includes(tags.retry_ordinal)
  && DEPLOYMENT_TYPES.includes(tags.deployment_type)
);

const projectSentryEventBody = (event, sdk) => {
  if (event.message !== CHROME_VIEWER_SENTRY_MESSAGE
    || !EVENT_ID_PATTERN.test(event.event_id || '')
    || !Number.isFinite(event.timestamp)
    || event.platform !== 'javascript'
    || event.level !== 'error'
    || typeof event.environment !== 'string'
    || typeof event.release !== 'string'
    || !validSentryTags(event.tags)) {
    return null;
  }

  return {
    event_id: event.event_id,
    timestamp: event.timestamp,
    platform: 'javascript',
    level: 'error',
    environment: event.environment,
    release: event.release,
    message: CHROME_VIEWER_SENTRY_MESSAGE,
    sdk,
    tags: sentryTagsFromFields(event.tags),
  };
};

export const projectChromeViewerSentryEvent = (event) => {
  if (event?.tags?.telemetry_contract !== CHROME_VIEWER_TELEMETRY_CONTRACT) {
    return event;
  }

  const projected = projectSentryEventBody(event, undefined);
  if (!projected) {
    return null;
  }
  delete projected.sdk;
  return projected;
};

const projectChromeViewerSentryEnvelope = (envelope) => {
  const [header, items] = Array.isArray(envelope) ? envelope : [];
  if (!header || !Array.isArray(items) || items.length !== 1) {
    return null;
  }
  const [itemHeader, event] = Array.isArray(items[0]) ? items[0] : [];
  if (itemHeader?.type !== 'event'
    || header.sdk?.name !== SENTRY_SDK.name
    || header.sdk?.version !== SENTRY_SDK.version
    || !isDateLike(header.sent_at)
    || header.event_id !== event?.event_id) {
    return null;
  }

  const projectedEvent = projectSentryEventBody(event, { ...SENTRY_SDK });
  if (!projectedEvent) {
    return null;
  }
  return [
    {
      event_id: header.event_id,
      sent_at: header.sent_at,
      sdk: { ...SENTRY_SDK },
    },
    [[{ type: 'event' }, projectedEvent]],
  ];
};

export const validateChromeViewerSentryEnvelope = (envelope) => {
  const projected = projectChromeViewerSentryEnvelope(envelope);
  const canonicalize = (value) => {
    if (Array.isArray(value)) {
      return value.map(canonicalize);
    }
    if (value && typeof value === 'object') {
      const result = {};
      Object.keys(value).sort().forEach((key) => {
        result[key] = canonicalize(value[key]);
      });
      return result;
    }
    return value;
  };

  return projected !== null
    && JSON.stringify(canonicalize(projected)) === JSON.stringify(canonicalize(envelope));
};

export const createChromeViewerSentryTransport = (transportFactory) => (options) => {
  const transport = transportFactory(options);
  return {
    ...transport,
    send(envelope) {
      const isTagged = envelope?.[1]?.some(
        (item) => item?.[1]?.tags?.telemetry_contract === CHROME_VIEWER_TELEMETRY_CONTRACT,
      );
      if (!isTagged) {
        return transport.send(envelope);
      }

      const projected = projectChromeViewerSentryEnvelope(envelope);
      if (!projected || !validateChromeViewerSentryEnvelope(projected)) {
        return Promise.resolve({});
      }
      return transport.send(projected);
    },
  };
};

const chromeViewerPostHogOptions = (apiHost) => ({
  api_host: apiHost || 'https://us.i.posthog.com',
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  disable_session_recording: true,
  disable_surveys: true,
  disable_external_dependency_loading: true,
  advanced_disable_flags: true,
  advanced_disable_feature_flags: true,
  advanced_disable_feature_flags_on_first_load: true,
  opt_out_useragent_filter: true,
  persistence: 'memory',
  person_profiles: 'never',
  request_batching: true,
  before_send: projectChromeViewerPostHogEvent,
});

export const createChromeViewerPostHogClient = ({
  apiHost,
  apiKey,
  PostHogClient = PostHog,
} = {}) => {
  if (!apiKey) return null;

  const client = new PostHogClient();
  client.init(apiKey, chromeViewerPostHogOptions(apiHost));
  return client;
};

export const createChromeViewerSentryClient = ({
  dsn,
  environment,
  release,
  sentrySdk = Sentry,
  transportFactory,
} = {}) => {
  if (!dsn || !environment || !release) return null;

  const client = new sentrySdk.BrowserClient({
    dsn,
    environment,
    release,
    defaultIntegrations: false,
    integrations: [],
    sendClientReports: false,
    stackParser: sentrySdk.defaultStackParser,
    _metadata: {
      sdk: { ...SENTRY_SDK },
    },
    transport: createChromeViewerSentryTransport(
      transportFactory || sentrySdk.makeFetchTransport,
    ),
    beforeSend: projectChromeViewerSentryEvent,
  });
  client.init();

  return {
    captureMessage(message, { level, tags } = {}) {
      const scope = new sentrySdk.Scope();
      scope.setClient(client);
      scope.setTags(tags || {});
      client.captureMessage(message, level, undefined, scope);
    },
    close: (...args) => client.close(...args),
    flush: (...args) => client.flush(...args),
  };
};

let configuredClients = {
  posthogClient: null,
  sentryClient: null,
};

export const configureChromeViewerTelemetry = ({
  posthogClient = null,
  sentryClient = null,
} = {}) => {
  configuredClients = {
    posthogClient,
    sentryClient,
  };
};

export const emitChromeViewerLifecycle = (
  stage,
  fields = {},
  { posthogClient, sentryClient } = {},
) => {
  try {
    const completeFields = {
      ...fields,
      page_boot_id: fields.page_boot_id || PAGE_BOOT_ID,
      deployment_type: fields.deployment_type || deploymentType(),
    };
    if (!validLifecycleFields(stage, completeFields)) {
      return false;
    }

    const properties = lifecycleProperties(stage, completeFields);
    try {
      const resolvedPostHogClient = posthogClient === undefined
        ? configuredClients.posthogClient
        : posthogClient;
      resolvedPostHogClient?.capture?.(
        CHROME_VIEWER_EVENT,
        properties,
        CHROME_VIEWER_CAPTURE_OPTIONS,
      );
    } catch {
      // Telemetry is best effort and must never alter viewer behavior.
    }
    if (stage === 'record_fetch_failed') {
      try {
        const resolvedSentryClient = sentryClient === undefined
          ? configuredClients.sentryClient
          : sentryClient;
        resolvedSentryClient?.captureMessage?.(CHROME_VIEWER_SENTRY_MESSAGE, {
          level: 'error',
          tags: sentryTagsFromFields(completeFields),
        });
      } catch {
        // PostHog remains authoritative when supplementary alerting is blocked.
      }
    }
    return true;
  } catch {
    return false;
  }
};
